import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { useMemo } from "react";
import { SubscriptionInvoice } from "../../mange-subscription-models";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { formatToCurrency } from "@shared/utils/formatters";
import { SubscriptionInvoicesActionMenu } from "./SubscriptionInvoicesActionMenu";

type Props = {
  invoices: Array<SubscriptionInvoice>;
  actionsEnabled: boolean;
  handleVoidInvoice?: (invoiceId: string) => void;
  handleRefundInvoice?: (invoiceId: string) => void;
};

const Container = styled.div`
  width: 100%;
`;

export const SubscriptionInvoicesList = (props: Props) => {
  const { invoices, actionsEnabled, handleVoidInvoice, handleRefundInvoice } =
    props;
  const theme = useTheme();

  const columns = useMemo(() => {
    const canVoidInvoice = (data: { status: string }) => {
      return data.status === "open" || data.status === "uncollectible";
    };
    const canRefundInvoice = (data: {
      status: string;
      amountDue: number;
      refunded: boolean;
    }) => {
      return data.status === "paid" && data.amountDue > 0 && !data.refunded;
    };

    const columns: GridColDef<Array<SubscriptionInvoice>[number]>[] = [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "createdAt",
        headerName: "Date",
        width: 120,
        renderCell: (params) => {
          return params.value
            ? format(new Date(params.value), "MM/dd/yyyy")
            : "-";
        },
      },
      {
        field: "amountDue",
        headerName: "Amount",
        width: 120,
        renderCell: (params) => {
          return `US${formatToCurrency(params.value / 100)}`;
        },
      },
      {
        field: "creditsUsed",
        headerName: "Credits used",
        width: 140,
        renderCell: (params) => {
          return `US${formatToCurrency(params.value / 100)}`;
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 100,
        renderCell: (params) => {
          return (
            <PaymentStatus
              status={params.row.refunded ? "refunded" : params.value}
            />
          );
        },
      },
      {
        field: "dueDate",
        headerName: "Due",
        width: 120,
        renderCell: (params) => {
          return params.value
            ? format(new Date(params.value), "MM/dd/yyyy")
            : "-";
        },
      },
      {
        field: "paidAt",
        headerName: "Paid on",
        width: 120,
        renderCell: (params) => {
          return params.value
            ? format(new Date(params.value), "MM/dd/yyyy")
            : "-";
        },
      },
    ];

    if (actionsEnabled) {
      columns.push({
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        width: 80,
        renderCell: (params) => (
          <SubscriptionInvoicesActionMenu
            onVoid={
              canVoidInvoice({ status: params.row.status })
                ? () => handleVoidInvoice?.(params.row.id)
                : undefined
            }
            onRefund={
              canRefundInvoice({
                status: params.row.status,
                amountDue: params.row.amountDue,
                refunded: params.row.refunded,
              })
                ? () => handleRefundInvoice?.(params.row.id)
                : undefined
            }
          />
        ),
      });
    }

    return columns;
  }, [handleVoidInvoice, handleRefundInvoice, actionsEnabled]);

  return (
    <Container>
      <DataGrid
        rows={invoices}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 12,
            },
          },
        }}
        pageSizeOptions={[12]}
        getRowId={(row) => new Date(row.createdAt).toISOString()}
        hideFooterPagination={invoices.length <= 12}
        disableRowSelectionOnClick
        sx={{
          border: `solid 1px ${theme.colors.border2}`,
          borderRadius: "0.75rem",
          padding: "0.5rem 1rem",
        }}
        disableColumnResize
      />
    </Container>
  );
};

const PaymentStatusStyled = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: ${(p) => p.theme.text.fontSizes.s};
  font-weight: 500;
  justify-self: flex-start;
  text-transform: capitalize;
`;

const PaymentStatus = ({
  status,
}: {
  status: "paid" | "draft" | "refunded" | string;
}) => {
  const theme = useTheme();

  const { color, backgroundColor } = useMemo(() => {
    switch (status) {
      case "paid":
        return {
          color: theme.colors.success,
          backgroundColor: `${theme.colors.success}33`,
        };
      case "refunded":
        return {
          color: theme.colors.primary,
          backgroundColor: `${theme.colors.primary}33`,
        };
      case "draft":
        return {
          color: theme.colors.warning,
          backgroundColor: `#f2c308`,
        };
      case "uncollectible":
        return {
          color: theme.colors.neutral,
          backgroundColor: `${theme.colors.neutral}33`,
        };
      case "void":
        return {
          color: theme.colors.neutral,
          backgroundColor: `${theme.colors.neutral}33`,
        };
      default:
        return {
          color: theme.colors.error,
          backgroundColor: `${theme.colors.error}33`,
        };
    }
  }, [theme.colors, status]);

  return (
    <PaymentStatusStyled style={{ color, backgroundColor }}>
      {status}
    </PaymentStatusStyled>
  );
};
